
/* --------------------------------------------------------------------------------------
   device-config.service.ts
   Copyright © 2024 Xerox Corporation. All Rights Reserved.

   Copyright protection claimed includes all forms and matters of copyrightable material
   and information now allowed by statutory or judicial law or hereinafter granted,
   including without limitation, material generated from the software programs which
   are displayed on the screen such as icons, screen display looks, etc.
--------------------------------------------------------------------------------------*/

import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { from, Subject, throwError, TimeoutError } from 'rxjs';
import { DeviceCapabilitiesService, DeviceNetworkService } from '../shared/eip';
import { PwaSessionService } from './pwa-session.service';
import { SessionService } from './session.service';
import { DeviceconfigParamsService } from './deviceconfig-params.service';
import { EnvironmentService } from './environment.service';
import { EnergysaverConfigService } from './energysaver-config.service';
import { LogService } from './log.service';

declare function xrxSessionGetSessionInfo(url: any, callback_success: any, callback_failure: any)
declare function xrxSessionParseGetSessionInfo(response: any)
declare function xrxConfigAsKioskDevice(url: any, admin: any, adminPassword: any, deviceID: any, deviceType:any, svrUrl: string,callback_success: any, callbck_failure: any);
declare  function xrxConfigSetPathwayDefaultApplication(url: any, pathway: string, name: string, vendor: string, isNative: boolean, admin: any, adminPassword: any,callback_success: any, callbck_failure: any)
declare function xrxWsSnmpSet( url: any, communityString: any, oidArr: string[],callback_success: any, callbck_failure: any)
declare function xrxFindElement(root: any, elements: any)
declare function xrxStringToDom(thedoc: any)
declare var XRXAccessConfig: any;
declare var XRXRegistration : any;

@Injectable({
  providedIn: 'root'
})
export class DeviceConfigService {
  t1
  private deviceModel
  private deviceSerial
  private deviceUrl: any
  private adminUsername: any
  private adminPassword: any
  private snmpCommunityWriteString: any
  private payload: string
  convertToKioskError = new Subject<any>()
  notification: Subject<any> = new Subject<any>()

  constructor(
    private http: HttpClient,
    private deviceNetworkService: DeviceNetworkService,
    private sessionService: SessionService,
    private pwaSessionService: PwaSessionService,
    private deviceCapabilitiesService: DeviceCapabilitiesService,
    private deviceConfigParamsService: DeviceconfigParamsService,
    private environmentService: EnvironmentService,
    private logService: LogService  ) {
    this.sessionService.getKioskSettings()
    this.sessionService.getDeviceInfo().subscribe(
      deviceInfo => {
        this.deviceSerial = deviceInfo.serial
      }
    )
    this.deviceUrl = this.sessionService.deviceUrl
  }

  doSetDeviceConfig() {
    this.logService.debug("doSetDeviceConfig called")
    this.sessionService.isSetDeviceConfig = false
    return this.deviceConfigParamsService.get()
      .pipe(take(1), switchMap(s => {
        return this.setDeviceConfig(s)
      })).pipe(timeout(30000),
        catchError((error) => { // Error...
          // Handle 'timeout' error
          if (error instanceof TimeoutError) {
            this.logService.debug("device-config.servic setDeviceConfig() TimeoutError catchError" + JSON.stringify(error))
            return throwError('Timeout Exception');
          }
          // Return other errors
          this.logService.debug("device-config.servic setDeviceConfig() catchError" + JSON.stringify(error))
          return throwError(error);
        }))
  }

  convertToKiosk(adminUser, adminPassword, snmpCommunityWriteString) {
    this.adminUsername = adminUser;
    this.adminPassword = adminPassword;
    this.snmpCommunityWriteString = snmpCommunityWriteString
    var serUrl = this.environmentService.customDomainUrl + '/JobLimitsAppServer.asmx'
    const result = new Promise((resolve, reject) => {
      xrxConfigAsKioskDevice(
        this.deviceUrl,
        this.adminUsername,
        this.adminPassword,
        this.deviceSerial,
        serUrl,
        this.deviceConfigParamsService.getDeviceType(),
        (env, response) => resolve(this.disableScreenSaver()),
        (env, error) => reject((error))
      )
    })

    return from(result)
  }

  private disableScreenSaver() {
    var oidArr = ["1.3.6.1.4.1.253.8.53.5.2.1.2.1", "i", "2325",
      "1.3.6.1.4.1.253.8.53.5.2.1.3.1", "s", "OI=1.3.6.1.4.1.253.8.53.13.2.1.6.1.180.105:OV=2:",
      "1.3.6.1.4.1.253.8.53.5.2.1.7.1", "s", this.adminPassword];
    return xrxWsSnmpSet(
      this.deviceUrl,
      this.snmpCommunityWriteString,
      oidArr,
      (env, response) => this.ListRegistrations(),
      (env, error) => this.handleSettingsMismatch(error)
    )
  }

  private ListRegistrations() {
    return XRXRegistration.xrxRegistrationListRegistrations({
      url: this.deviceUrl,
      callback_success: (env, response) => {
        (this.pwaSessionService.GetDeviceControler() == 'Discovery Controller')? this.setNoPrompting() : "",
        this.getRegistrations(response)
      },
      callback_failure: (env, error) => this.handleConfigError(error),
    });
  }

  private setNoPrompting() {
    var serUrl = this.environmentService.customDomainUrl + '/JobLimitsAppServer.asmx'
    this.payload = '<?xml version="1.0" encoding="utf-8"?><xrx:AccountingMethodConfigurationPayload xmlns:SOAP-ENV="http://schemas.xmlsoap.org/soap/envelope/" xmlns:SOAP-ENC="http://schemas.xmlsoap.org/soap/encoding/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xmlns:xsd="http://www.w3.org/2001/XMLSchema" xmlns:xop="http://www.w3.org/2004/08/xop/include" xmlns:c14n="http://www.w3.org/2001/10/xml-exc-c14n#" xmlns:wsu="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-utility-1.0.xsd" xmlns:ds="http://www.w3.org/2000/09/xmldsig#" xmlns:wsse="http://docs.oasis-open.org/wss/2004/01/oasis-200401-wss-wssecurity-secext-1.0.xsd" xmlns:xrx="http://www.xerox.com/webservices/office/accessconfig/1"><xrx:AccountingMethods><xrx:JBA><xrx:AccountingMethodEnabled>true</xrx:AccountingMethodEnabled><xrx:AccountingMethodInfoReadOnly><xrx:AccountingName>JobBasedAccounting</xrx:AccountingName><xrx:isNative>true</xrx:isNative><xrx:supportsJobLimits>true</xrx:supportsJobLimits></xrx:AccountingMethodInfoReadOnly><xrx:Validation>false</xrx:Validation><xrx:JBAPromptDetails><xrx:DisplayPrompt>false</xrx:DisplayPrompt><xrx:Label>UserID</xrx:Label><xrx:DefaultValue>'
      + this.deviceSerial + '</xrx:DefaultValue><xrx:MaskEntries>false</xrx:MaskEntries><xrx:PromptIndex>0</xrx:PromptIndex></xrx:JBAPromptDetails><xrx:JBAPromptDetails><xrx:DisplayPrompt>false</xrx:DisplayPrompt><xrx:Label>AccountID</xrx:Label><xrx:DefaultValue>'
      + this.deviceSerial + '</xrx:DefaultValue><xrx:MaskEntries>false</xrx:MaskEntries><xrx:PromptIndex>1</xrx:PromptIndex></xrx:JBAPromptDetails><xrx:JBAByService><xrx:JBAPromptsByService><xrx:JBAPromptByService><xrx:PromptOptionName>CopyServicePrompt</xrx:PromptOptionName><xrx:PromptOptionValue>Prompt</xrx:PromptOptionValue></xrx:JBAPromptByService><xrx:JBAPromptByService><xrx:PromptOptionName>PrintServicePrompt</xrx:PromptOptionName><xrx:PromptOptionValue>Prompt</xrx:PromptOptionValue></xrx:JBAPromptByService><xrx:JBAPromptByService><xrx:PromptOptionName>ScanServicePrompt</xrx:PromptOptionName><xrx:PromptOptionValue>Prompt</xrx:PromptOptionValue></xrx:JBAPromptByService><xrx:JBAPromptByService><xrx:PromptOptionName>FaxServicePrompt</xrx:PromptOptionName><xrx:PromptOptionValue>Prompt</xrx:PromptOptionValue></xrx:JBAPromptByService><xrx:JBAPromptByService><xrx:PromptOptionName>EIPApplicationsPrompt</xrx:PromptOptionName><xrx:PromptOptionValue>NoPrompt</xrx:PromptOptionValue></xrx:JBAPromptByService></xrx:JBAPromptsByService></xrx:JBAByService></xrx:JBA><xrx:XSA><xrx:AccountingMethodEnabled>false</xrx:AccountingMethodEnabled><xrx:AccountingMethodInfoReadOnly><xrx:AccountingName>XeroxStandardAccounting</xrx:AccountingName><xrx:isNative>true</xrx:isNative><xrx:supportsJobLimits>false</xrx:supportsJobLimits></xrx:AccountingMethodInfoReadOnly><xrx:ShowCustomerAccountNumber>false</xrx:ShowCustomerAccountNumber></xrx:XSA><xrx:FDI><xrx:AccountingMethodEnabled>false</xrx:AccountingMethodEnabled><xrx:AccountingMethodInfoReadOnly><xrx:AccountingName>AuxillaryInterface</xrx:AccountingName><xrx:isNative>true</xrx:isNative><xrx:supportsJobLimits>false</xrx:supportsJobLimits></xrx:AccountingMethodInfoReadOnly></xrx:FDI></xrx:AccountingMethods><xrx:JobLimitsSettings><xrx:JobLimitsMethodSelected>JobBasedAccounting</xrx:JobLimitsMethodSelected><xrx:AuthorizationType><xrx:CopyJob>PreAuth</xrx:CopyJob><xrx:PrintJob>PreAuth</xrx:PrintJob></xrx:AuthorizationType><xrx:AdditionalAuthorizationTypes><xrx:AdditionalAuthorizationType><xrx:AuthorizationName>EMailJob</xrx:AuthorizationName><xrx:AuthorizationValue>PreAuth</xrx:AuthorizationValue></xrx:AdditionalAuthorizationType><xrx:AdditionalAuthorizationType><xrx:AuthorizationName>ScanJob</xrx:AuthorizationName><xrx:AuthorizationValue>PreAuth</xrx:AuthorizationValue></xrx:AdditionalAuthorizationType><xrx:AdditionalAuthorizationType><xrx:AuthorizationName>FaxJob</xrx:AuthorizationName><xrx:AuthorizationValue>PreAuth</xrx:AuthorizationValue></xrx:AdditionalAuthorizationType></xrx:AdditionalAuthorizationTypes><xrx:ServerURL>'
      + serUrl + '</xrx:ServerURL><xrx:Timeout>30</xrx:Timeout><xrx:PrintErrorHandling>None</xrx:PrintErrorHandling></xrx:JobLimitsSettings><xrx:USBAccess><xrx:PrintFromUSB>true</xrx:PrintFromUSB><xrx:ScanToUSB>true</xrx:ScanToUSB></xrx:USBAccess></xrx:AccountingMethodConfigurationPayload>'

    XRXAccessConfig.xrxAccessConfigSetAccountingMethodConfiguration({
      url: this.deviceUrl,
      payload: this.payload,
      username: this.adminUsername,
      password: this.adminPassword,
      passwordType: "PasswordDigest",
      callback_success: (env, response) => (response),
      callback_failure: (env, error) => this.handleConfigError(),
      timeout: 0
    })
  }

  private getRegistrations(response) {
    const list = XRXRegistration.xrxRegistrationParseListRegistrations(response)
    for (let index = 0; index < list.length; index++) {
      const element = list[index];
      if ((this.pwaSessionService.GetDeviceControler() == 'Discovery Controller') && (element.name == 'Copy' || element.name == 'Email' || element.name == 'Print From' || element.name == 'ScanTo' || element.name == 'NetworkScanning' || element.name == 'IDCardCopy' || element.name == 'Fax' || element.name == 'ServerFax')) {
        this.hideNativeApps(element.name);
      }
      if (!(element.name.includes(this.sessionService.appName) || element.name.includes('Jobs') || element.name.includes('Device') || element.name.includes('Copy') || element.name.includes('Email'))) {
        XRXRegistration.xrxRegistrationGetRegistration({
          url: this.deviceUrl,
          name: element.name,
          callback_success: (env, response) => this.hideApps(response),
          callback_failure: (env, error) => this.handleConfigError()
        })
      }
    }
    setTimeout(() => {
      this.setKioskAppAsDefault()
    }, 5000);
    return list
  }

  private hideApps(response) {
    const registration = XRXRegistration.xrxRegistrationParseGetRegistration(response)
    if (registration.SmallIconUrl != null && registration.IsNative != 'true') {
      registration.IsDisabled = true;
      registration.SmallIconUrl = '';
      registration.ToolsIconUrl = '';
      registration.Url = '';
      registration.DescriptionUrl = '';
      return XRXRegistration.xrxRegistrationUpdateRegistration({
        url: this.deviceUrl,
        registration: registration,
        admin: this.adminUsername,
        adminPassword: this.adminPassword,
        passwordType: "PasswordDigest",
        callback_success: (env, response) => (response),
        callback_failure: (env, error) => this.handleConfigError()
      })
    }
  }

  private hideNativeApps(appName) {
    return XRXAccessConfig.xrxAccessConfigSetAccessConfigInfo({
      url: this.deviceUrl,
      appName: appName,
      accessType: 'xeHIDDEN',
      userList: '',
      username: this.adminUsername,
      password: this.adminPassword,
      passwordType: "PasswordDigest",
      callback_success: (env, response) => (response),
      callback_failure: (env, error) => this.handleConfigError(),
      timeout: 0
    });
  }

  private setKioskAppAsDefault() {
    var appName = this.sessionService.appName
    return xrxConfigSetPathwayDefaultApplication(
      this.deviceUrl,
      "Services",
      appName,
      "Xerox EIP Development",
      false,
      this.adminUsername,
      this.adminPassword,
      (env, response) => this.notification.next("DeviceConfigComplete"),
      (env, error) => this.handleConfigError(error)
    )
  }

  restartMFD(snmpCommunityWriteString :string) {
    return xrxWsSnmpSet(
      this.deviceUrl,
      snmpCommunityWriteString,
      ["1.3.6.1.2.1.43.5.1.1.3.1", "i", "4"],
      (env, response) => this.handleRestartSuccess(),
      (env, error) => this.handleRestartError(error)
    )
  }

  private handleConfigError(error?: any) {
    return throwError(error)
  }

  private handleSettingsMismatch(error: any) {
    var errorName = this.parseErrorResponse(error)
    if(JSON.stringify(error).includes("SNMPInvalidCommunityString")) this.convertToKioskError.next("SNMPInvalidCommunityString")
    else if (errorName == 'SNMPBadValueFault') this.ListRegistrations()
    else {
      this.convertToKioskError.next(JSON.stringify(errorName))
      return throwError(error)
    }
  }

  handleRestartError(error) {
    this.convertToKioskError.next(error)
  }

  parseErrorResponse(response) {
    var data = null;
    if ((response != null) && (response != ""))
    try {
      data = xrxFindElement(xrxStringToDom(response), ["faultstring"]);
      return data.textContent;
    } catch (error) {
      try {
         return this.parseStringSoapErrorResponse(response)
      } catch (error) {
        return ""
      }
    }
  }

  private handleRestartSuccess() {
    return from("success")
  }

  parseStringSoapErrorResponse(response) {
    var subcode = "";
    if ((typeof (response) != "undefined") && (response != null)) {
      var index = response.indexOf("Subcode");
      if (index > 0)
        if ((index = response.indexOf("Value", index)) > 0)
          if ((index = response.indexOf(">", index)) > 0)
            subcode = response.substring(index + 1, response.indexOf("<", index));
    }
    var error = subcode.split(":")
    return error[1]
  }

  private setDeviceConfig(configBody) {
    this.t1 = Date.now()
    const endpoint = this.environmentService.kioskServerUrl + '/api/Kiosk/SetDeviceConfig'
    let headers: HttpHeaders = new HttpHeaders()
    headers = headers.append('sessionID', sessionStorage.getItem("sessionID"));
    headers = headers.append('Content-Type', 'application/json')
    const params = new HttpParams().append('deviceID', this.deviceSerial);
    const body = configBody
    const deviceConfigData = JSON.stringify(body)
    return this.http.post(endpoint, deviceConfigData, { headers, params }).pipe(timeout(10000))
  }

  getAdminStatus(){
    const result = new Promise((resolve, reject) => {
      xrxSessionGetSessionInfo(
        "http://127.0.0.1",
          (env, response) => resolve( xrxSessionParseGetSessionInfo(response)),
          (env, response, status) => reject({env, response, status})
        )
      })
      return from(result)
  }

  setTrayInformationAPI(){
    return this.deviceCapabilitiesService.setUpdateTrayInformation()
    .pipe(switchMap(s=> this.setTrayInfo(s)))
  }

  public setTrayInfo(data) {
    const endpoint = this.environmentService.kioskServerUrl + '/api/Kiosk/SetTrayInfo'
    let headers: HttpHeaders = new HttpHeaders()
    headers = headers.append('sessionID', sessionStorage.getItem("sessionID"));
    const params = new HttpParams().append('deviceID', this.deviceSerial);
    const body = {
      'inputTrays':data.trays,
      'stapleFinishingsSupported':data.finisher.stapleFinishingsSupported,
      'punchFinishingsSupported':data.finisher.punchFinishingsSupported
    }
    const trayData = body
    return this.http.post(endpoint, trayData, { headers, params }).pipe(timeout(this.environmentService.timeOutValue * 1000));
  }

  checkAuthentication(adminPassword) {
    var appName = this.sessionService.appName
    const result = new Promise((resolve, reject) => {
      xrxConfigSetPathwayDefaultApplication(
        this.deviceUrl,
        "Services",
        appName,
        "Xerox EIP Development",
        false,
        this.sessionService.userName,
        adminPassword,
        (env, response) => resolve(response),
        (env, error) => reject(error)
      )
    })
    return from(result)
  }
}
